import React, { useCallback } from 'react';
import { useScrollTrigger } from '@mui/material';
import { PageAnchors } from './Header/PageAnchors';
import Header from './Header/Header';
import ErrorBoundary from '../ErrorBoundary';
import Breadcrumbs from '../Breadcrumbs';
import { HEADER_TRANSITION, HEADER_HEIGHT } from './Header/Header';
import classNames from 'classnames';

interface PageLayoutProps {
  title: string;
  links: Array<{
    id: string;
    name: string;
    nameMobile: string;
    section: string;
    ref: React.RefObject<any>;
  }>;
  sectionScrollRef: React.RefObject<any>;
  visibleSection: string | null;
  breadcrumbs?: {
    neighborhoodId: string;
    neighborhoodName: string;
    buildingName?: string;
    buildingId?: string;
    buildingPage?: boolean;
    preConstructionPage?: boolean;
    unitPage?: boolean;
    unitName?: string;
    unitUrl?: string;
    neighborhoodPage?: boolean;
  };
  isDesktop: boolean;
  scrollPastRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
  headerContent: React.ReactNode;
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  title,
  links,
  visibleSection,
  breadcrumbs,
  isDesktop,
  children,
  scrollPastRef,
  headerContent,
}) => {
  const isSticky = useScrollTrigger();

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
      e.preventDefault();
      const sectionRef = links.find((link) => link.id === sectionId)?.ref;
      if (sectionRef?.current) {
        const currentScroll = window.pageYOffset;
        const targetScroll = sectionRef.current.offsetTop;

        // Only apply header offset when scrolling up
        const headerHeight =
          targetScroll < currentScroll
            ? isDesktop
              ? HEADER_HEIGHT.desktop
              : isDesktop === false
              ? HEADER_HEIGHT.mobile
              : HEADER_HEIGHT.tablet
            : 0;

        // Add offset to account for spacing
        const offset = 50;

        window.scrollTo({
          top: targetScroll - headerHeight - offset,
          behavior: 'smooth',
        });
      }
    },
    [links, isDesktop]
  );

  return (
    <>
      <Header hideOnScroll={true} />
      {isDesktop && breadcrumbs && (
        <ErrorBoundary>
          <Breadcrumbs {...breadcrumbs} />
        </ErrorBoundary>
      )}
      {headerContent}
      <div
        className={classNames(
          'sticky z-100 bg-white ml-auto md:ml-0 flex md:flex-wrap mt-[17px] md:mt-0',
          {
            "before:content-[''] before:absolute before:top-[-100px] before:left-0 before:w-full before:h-[100px] before:bg-white":
              isSticky && isDesktop,
          }
        )}
        style={{
          top: isSticky
            ? 0
            : `${
                isDesktop
                  ? HEADER_HEIGHT.desktop
                  : isDesktop === false
                  ? HEADER_HEIGHT.mobile
                  : HEADER_HEIGHT.tablet
              }px`,
          transition: `top ${HEADER_TRANSITION.duration}ms ${HEADER_TRANSITION.easing}`,
        }}
      >
        <PageAnchors
          title={title}
          links={links}
          visibleSection={visibleSection}
          scrollPastRef={scrollPastRef}
          handleLinkClick={handleLinkClick}
          isHeaderHidden={isSticky}
        />
      </div>
      {children}
    </>
  );
};
